import { Inject, Injectable } from '@angular/core';
import { IWindowService } from 'angular';
import * as _ from 'lodash';
import { Mixin } from '../../helpers/class-decorators/mixin-decorator.helper';
import { TValidPartnerTypes } from '../../typings/interfaces/partner.interface';
import {
  IUser,
  IUserMini,
  IUserMiniWithRole,
  TEffectiveRole,
  TValidUserRoles,
} from '../../typings/interfaces/user.interface';
import { ObjectHash } from '../object-hash/object-hash.service';
import { UserInactivity } from '../user-inactivity/user-inactivity.service';
import { ToggleService } from './../toggle/toggle.service';
import { ImUserCore, IRoleTypesMapper } from './im-user/im-user-core';
import { ImUserSchool } from './im-user/im-user-school';
import { ImUserShelter } from './im-user/im-user-shelter';
import { userCate } from './im-user/im-user.helper';

export interface IStateData {
  roles: TValidUserRoles[];
  toggles?: string[];
}

// Note-jchu: removed 'sdc_admin', 'super_admin', 'etl_super_admin' from the following constants
export const IM_USER_CONSTANTS = {
  // roles for all cluster users - school cluster, shelter cluster
  CLUSTER_ROLES: ['cluster_admin', 'network_and_school', 'network_only', 'shelter_cluster_admin', 'shelter_only'],

  // roles for all cluster admin users - school cluster, shetler cluster
  CLUSTER_ADMIN_ROLES: ['cluster_admin', 'shelter_cluster_admin'],

  // roles only for shelter user
  SHELTER_ROLES: ['shelter_view_all'],

  // roles only for school user
  SCHOOL_ROLES: ['school_admin', 'delegated_school_admin', 'edit_all', 'edit_caseload', 'view_all', 'view_caseload'],

  // Fields that are required for the user to be considered "valid". Not a comprehensive list.
  REQUIRED_FIELDS: ['nvRole', '_id'],

  // roles for all admins - cluster admins, school admins and shelter admins
  ADMIN_ROLES: [
    'school_admin',
    'cluster_admin',
    'delegated_school_admin',
    'edit_all',
    'edit_caseload',
    'shelter_cluster_admin',
  ],

  // roles only for school user and school cluster user
  SCHOOL_CLUSTER_OR_DELEGATED_ADMIN: ['school_admin', 'cluster_admin', 'delegated_school_admin'],

  // roles for school user, shelter user, cluster user on school/shelter level
  // view only, does not have editing previliges at all
  VIEW_ONLY_ROLE_TYPES: ['view_caseload', 'view_all', 'shelter_view_all', 'shelter_cluster_admin'],

  // roles for school user, shelter user, cluster user on school/shelter level
  EDITING_ROLES: ['school_admin', 'cluster_admin', 'delegated_school_admin', 'edit_caseload', 'edit_all'],

  // roles for school user
  CASELOAD_ROLES: ['view_caseload', 'edit_caseload'],

  PARTNER_TYPES: ['school', 'shelter'],
};

/* istanbul ignore next */
@Injectable()
@Mixin([ImUserSchool, ImUserShelter, ImUserCore])
export class ImUser {
  constructor(
    @Inject('$state') private $state,
    @Inject('$window') private $window: IWindowService,
    @Inject('Rollbar') private Rollbar,
    private objectHash: ObjectHash,
    private toggleService: ToggleService,
    private userInactivity: UserInactivity,
  ) {}

  // If you add new methods or properties in ImUserSchool, ImUserShelter or ImUserCore,
  // you also need to declare those newbies here:

  /* ImUserSchool type declaration */
  isSchoolUser: (user: IUser) => boolean;
  isSchoolClusterOrDelegatedAdmin: (user: IUser) => boolean;
  isSchoolWideEditor: (user: IUser) => boolean;
  getPrimarySchool: (user: IUser) => string;
  canAccessSync: (user: IUser) => boolean;
  private canViewSchoolNetworkSettings: (user: IUser | IUserMiniWithRole) => boolean;
  private static checkSchoolPortfolioAccess: (user: IUser) => boolean;
  private canSeeSchoolNetworkStuLvl: (user: IUser) => boolean;
  private static checkSchoolClusterUserManageStatus: (user: IUser) => boolean;

  /* ImUserShelter type declaration */
  isShelterUser: (user: IUser) => boolean;
  private canViewShelterNetworkSettings: (user: IUser | IUserMiniWithRole) => boolean;
  private static checkShelterPortfolioAccess: (user: IUser) => boolean;
  private canSeeShelterNetworkStuLvl: (user: IUser) => boolean;
  private static checkShelterClusterUserManageStatus: (user: IUser) => boolean;

  /* ImUserCore type declaration */
  hasPartnerId: (user: IUser, partnerId: string, partnerType?: TValidPartnerTypes) => boolean;
  getRoleTypes: (user: IUser | IUserMiniWithRole) => IRoleTypesMapper[];
  getRoleType: (user: IUser | IUserMiniWithRole, partnerType?: TValidPartnerTypes) => TEffectiveRole;
  getRoleTypeOnAllLevels: (user: IUser, partnerType?: TValidPartnerTypes) => TEffectiveRole;
  getRoleTypeOnPartnerLevel: (user: IUser, partnerType?: TValidPartnerTypes) => TEffectiveRole;
  recalcRoleTypeOnPartnerLevel: (
    params: { user: IUser; partnerType: TValidPartnerTypes; stateOrUrl: string | object },
  ) => TEffectiveRole;
  recalcRoleTypeOnSchoolLevel: (user: IUser, schoolId: string) => TEffectiveRole;

  /* OWN METHODS */
  isClusterUser(user: IUser | IUserMiniWithRole, partnerType: TValidPartnerTypes = 'school'): boolean {
    const role = this.getRoleType(user, partnerType);
    return _.includes(IM_USER_CONSTANTS.CLUSTER_ROLES, role);
  }

  isHybridUser(user: IUser | IUserMiniWithRole): boolean {
    const schoolRole = this.getRoleType(user, 'school');
    const shelterRole = this.getRoleType(user, 'shelter');
    return !!(schoolRole && shelterRole);
  }

  isAdmin(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const role = this.getRoleType(user, partnerType);
    return _.includes(IM_USER_CONSTANTS.ADMIN_ROLES, role);
  }

  isClusterAdmin(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const role = this.getRoleType(user, partnerType);
    return _.includes(IM_USER_CONSTANTS.CLUSTER_ADMIN_ROLES, role);
  }

  isEditOrViewAll(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const editViewRoles = ['edit_all', 'view_all'];
    const role = this.getRoleType(user, partnerType);
    return _.includes(editViewRoles, role);
  }

  isCaseloadUser(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const role = this.getRoleType(user, partnerType);
    return _.includes(IM_USER_CONSTANTS.CASELOAD_ROLES, role);
  }

  isNoAccessUser(user: IUser | IUserMiniWithRole, partnerType: TValidPartnerTypes = 'school'): boolean {
    const role = this.getRoleType(user, partnerType);
    const noAccessUser = ['school', 'cluster', 'shelter'].includes(role);
    return noAccessUser;
  }

  isEmpty(user: IUser): boolean {
    let ret = false;
    _.each(IM_USER_CONSTANTS.REQUIRED_FIELDS, function(key) {
      if (_.isUndefined(user[key])) {
        ret = true;
      }
    });

    return ret;
  }

  // for school user, only allows school admin, school delegated admin, edit_all and edit_caseload
  // for school cluster user, only allows cluster admin, school_admin/edit_all (given a school Id)
  // for all types of shelter user and shelter cluster user, returns false (MVP)
  isEditingUser(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const roleType = this.getRoleTypeOnAllLevels(user, partnerType);
    return _.includes(IM_USER_CONSTANTS.EDITING_ROLES, roleType);
  }

  // for school user, only allows 'view_caseload' and 'view_all'
  // for school cluster user, only allows view_all (given a school Id)
  // for all types of shelter user and shelter cluster user, returns true (MVP)
  isViewType(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const roleType = this.getRoleTypeOnAllLevels(user, partnerType);
    return _.includes(IM_USER_CONSTANTS.VIEW_ONLY_ROLE_TYPES, roleType);
  }

  isRoleAuthorized({ user, rolesThatCanAccessRoute, partnerType, stateOrUrl }): boolean {
    const unrestrictedRoute = rolesThatCanAccessRoute.includes('all');
    if (unrestrictedRoute) {
      return true;
    }
    const recalced = this.recalcRoleTypeOnPartnerLevel({ user, partnerType, stateOrUrl });
    const authorized = !!_.find(rolesThatCanAccessRoute, (_role: TValidUserRoles) => {
      if (_role && typeof _role === 'object') {
        const type = Object.keys(_role)[0];
        if (type === recalced) {
          const checkFunc = _role[type].bind(ImUser) as (user: IUser, partnerType: TValidPartnerTypes) => boolean;
          const res = checkFunc(user, partnerType);
          return res;
        } else {
          return false;
        }
      } else {
        return _role === recalced;
      }
    });
    return authorized;
  }

  // EMAIL RELATED:
  getEffectiveEmail(user: IUser | IUserMini | IUserMiniWithRole) {
    return user.gafeEmail || user.doeEmail;
  }

  getGafeEmail(user: IUser) {
    return user.gafeEmail;
  }

  getGafeEmailDomain(user: IUser) {
    const gafeEmail = this.getGafeEmail(user);
    const domain = gafeEmail && gafeEmail.split('@')[1].trim();
    return domain;
  }

  // NAME RELATED:

  // Defaults to preferred names
  getFullName(user: IUser) {
    const first = user.preferredName.firstName || user.name.firstName;
    const last = user.preferredName.lastName || user.name.lastName;
    return `${last}, ${first}`;
  }

  getFullNameFromMini(userMini: IUserMini) {
    const first = userMini.firstName;
    const last = userMini.lastName;
    if (!first && !last) return '(None)';
    return `${last}, ${first}`;
  }

  getFullNameWithEmailFromMini(userMini: IUserMini) {
    const firstLast = this.getFullNameFromMini(userMini);
    const effectiveEmail = this.getEffectiveEmail(userMini);
    return effectiveEmail ? `${firstLast} (${effectiveEmail})` : firstLast;
  }

  getHumanLastLogin(user: IUser) {
    let fo = user.lastLogin;
    fo = moment(fo).format('M/DD/YY h:mmA');
    return fo;
  }

  getProfilePic(user: IUser) {
    return user.picture;
  }

  // Returns only the fields that align to the backend UserMini schema
  toMiniUser(user: IUser) {
    return {
      userId: user._id,
      firstName: (user.preferredName && user.preferredName.firstName) || user.name.firstName,
      lastName: (user.preferredName && user.preferredName.lastName) || user.name.lastName,
      gafeEmail: user.gafeEmail,
      doeEmail: user.doeEmail,
      dhsEmail: user.dhsEmail,
    };
  }

  initializeGoogleAnalytics(user: IUser) {
    let userAnalyticsHash = user.userAnalyticsHash;

    if (!userAnalyticsHash) {
      userAnalyticsHash = this.objectHash.createHash(user._id);
      const err = new Error(
        `User ${user._id} is missing a "userAnalyticsHash" in database. User was assigned ` +
          `a "userAnalyticsHash" of "${userAnalyticsHash}" on the client. ` +
          `Update user in database with this value.`,
      );
      this.Rollbar.error(`Missing "userAnalyticsHash": `, err);
    }

    // Set Google analytics userId & as dimension1 for querying ease
    this.$window.ga('set', 'userId', userAnalyticsHash);
    this.$window.ga('set', 'dimension1', userAnalyticsHash);
  }

  // Only for ui-router that has states
  canAccessState(user: IUser, stateName): boolean {
    const state = this.$state.get(stateName);
    if (state) {
      const stateData: IStateData = state.data || {};
      const { roles, toggles } = stateData;

      // All states must at least have or inherit a `data.roles` value.
      // if a `data` object is not defined for a state, it will inherit `data` from the parent state (CM).
      if (!roles) throw new Error(`"${stateName}" state does not have required "data.roles" property.`);

      // Check for toggle access to state (CM).
      if (toggles) {
        const stateWithTogglesIsAccessible = this.canAccessStateWithToggles(user, stateData);

        if (!stateWithTogglesIsAccessible) return false;
      }

      const userRoleCanAccessState = this.isRoleAuthorized({
        user,
        rolesThatCanAccessRoute: roles,
        partnerType: 'school',
        stateOrUrl: state,
      });

      // Role permissioning result (CM).
      return userRoleCanAccessState;
    } else {
      const err = new Error(`This view contains reference to a state that is not registered: "${stateName}"`);

      this.Rollbar.error('State not registered referenced ', err);
      console.warn('State not registered referenced.', err);

      return true;
    }
  }

  canAccessStateWithToggles(user: IUser, stateData: IStateData): boolean {
    let stateCanBeAccessed: boolean = true; // base case (no toggles for state) (CM).

    if (stateData && stateData.toggles) {
      // if state has toggles, check that they are all ON (CM).
      _.each(stateData.toggles, toggle => {
        if (!this.isToggleOn(toggle)) stateCanBeAccessed = false;
      });
    }

    return stateCanBeAccessed;
  }

  isToggleOn(toggle: string): boolean {
    const toggleState = this.toggleService.getToggleState(toggle);

    return toggleState;
  }

  generateNullUser(): IUserMini {
    const nullUserTemplate = {
      userId: null,
      gafeEmail: null,
      doeEmail: null,
      dhsEmail: null,
      firstName: null,
      lastName: null,
    };
    return _.cloneDeep(nullUserTemplate);
  }

  // View only users do not have permission to view wizards (CM).
  // hence, view only users by default are sent to the grid for an sdc.
  getDefaultSdcView(user: IUser): 'grid' | 'wizard' {
    return this.isViewType(user) ? 'grid' : 'wizard';
  }

  // copied logic from currentUser resolve in main.route.ts
  setCurrentUserSession(currentUser: IUser): void {
    this.userInactivity.start();
    this.$window.currentUser = currentUser;
    this.initializeGoogleAnalytics(currentUser);
  }

  // copied logic from currentUser resolve in main.route.ts
  setRollbarConfig(currentUser: IUser): void {
    // Guard against Rollbar error reporting until necessary payload is available
    const config = {
      payload: {
        nv_authorizationStatus: currentUser.authorizationStatus,
        // school
        nv_role: currentUser.nvRole.type,
        nv_delegatedRole: currentUser.delegatedRole,
        // shelter
        nv_roleShelter: currentUser.nvRoleShelter && currentUser.nvRoleShelter.type,
        nv_delegatedRoleShelter: currentUser.delegatedRoleShelter,
      },
      verbose: true, // console log errors sent to Rollbar (Carlos)
    };
    this.Rollbar.configure(config);
  }

  /** START OF FEATURE BASED */

  // Decide if cluster user can access network settings -> cluster user permissions view
  // school cluster: used in network-left-side-nav.component.ts
  // shelter MVP: should be used in shelter picker component
  canViewNetworkSettings(user: IUser | IUserMiniWithRole, partnerType: TValidPartnerTypes = 'school'): boolean {
    const cated = userCate(user, partnerType);
    return cated({
      school: this.canViewSchoolNetworkSettings,
      shelter: this.canViewShelterNetworkSettings,
    });
  }

  // Decide if shows pill toggle to allow user switch between net dash view and single partner view
  // school cluster: used in network-left-side-nav.component.ts
  // shelter MVP: used in three col layout component
  canSeePillToggle(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    return ImUser.checkPortfolioAccess(user, partnerType);
  }

  // Decide if cluster user can access student lvl from mid lvl in net dash view
  // only allow cluster admin and full portfolio cluster user to access student level
  canSeeNetworkStuLvl(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const cated = userCate(user, partnerType);
    return cated({
      school: this.canSeeSchoolNetworkStuLvl.bind(this),
      shelter: this.canSeeShelterNetworkStuLvl.bind(this),
    });
  }

  // Decide if cluster user has at least one school/shelter to view or edit
  // if none, returns false
  static checkPortfolioAccess(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const cated = userCate(user, partnerType);
    return cated({
      school: this.checkSchoolPortfolioAccess.bind(this),
      shelter: this.checkShelterPortfolioAccess.bind(this),
    });
  }

  // Decide if cluster user can manage users
  static checkClusterUserManageStatus(user: IUser, partnerType: TValidPartnerTypes = 'school'): boolean {
    const cated = userCate(user, partnerType);
    return cated({
      school: this.checkSchoolClusterUserManageStatus.bind(this),
      shelter: this.checkShelterClusterUserManageStatus.bind(this),
    });
  }

  /** END OF FEATURE BASED */
}
